import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { t } from '../../../utils/translations';

const MultilingualNameFields = ({ 
  formData, 
  onFieldChange, 
  errors = {},
  disabled = false 
}) => {
  const [activeLanguage, setActiveLanguage] = useState('ar');

  const languages = [
    { code: 'ar', label: 'العربية', flag: '🇸🇾', dir: 'rtl' },
    { code: 'en', label: 'English', flag: '🇺🇸', dir: 'ltr' },
    { code: 'tr', label: 'Türkçe', flag: '🇹🇷', dir: 'ltr' }
  ];

  const handleLanguageChange = (langCode) => {
    setActiveLanguage(langCode);
  };

  const getCurrentLanguage = () => {
    return languages?.find(lang => lang?.code === activeLanguage);
  };

  const getFieldValue = (fieldName) => {
    return formData?.[`${fieldName}_${activeLanguage}`] || '';
  };

  const handleFieldChange = (fieldName, value) => {
    onFieldChange(`${fieldName}_${activeLanguage}`, value);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
          <Icon name="Globe" size={20} className="text-secondary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground font-arabic">
            {t('officeFormComponents.positionInformation')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('officeFormComponents.enterPositionDetails')}
          </p>
        </div>
      </div>
      {/* Language Tabs */}
      <div className="flex space-x-1 bg-muted p-1 rounded-lg">
        {languages?.map((language) => (
          <Button
            key={language?.code}
            variant={activeLanguage === language?.code ? 'default' : 'ghost'}
            size="sm"
            className="flex-1 justify-center"
            onClick={() => handleLanguageChange(language?.code)}
            disabled={disabled}
          >
            <span className="mr-2">{language?.flag}</span>
            <span className="text-sm">{language?.label}</span>
          </Button>
        ))}
      </div>
      {/* Current Language Indicator */}
      <div className="flex items-center justify-between p-3 bg-accent/5 rounded-lg border border-accent/20">
        <div className="flex items-center space-x-3">
          <span className="text-lg">{getCurrentLanguage()?.flag}</span>
          <div>
            <p className="text-sm font-medium text-foreground">
              {t('officeFormComponents.editingIn', { language: getCurrentLanguage()?.label })}
            </p>
            <p className="text-xs text-muted-foreground">
              {getCurrentLanguage()?.dir === 'rtl' ? t('officeFormComponents.rightToLeftText') : t('officeFormComponents.leftToRightText')}
            </p>
          </div>
        </div>
        <Icon 
          name={getCurrentLanguage()?.dir === 'rtl' ? 'AlignRight' : 'AlignLeft'} 
          size={16} 
          className="text-accent"
        />
      </div>
      {/* Form Fields */}
      <div 
        className="space-y-4"
        dir={getCurrentLanguage()?.dir}
      >
        <Input
          label={t('officeFormComponents.positionName')}
          type="text"
          placeholder={t('officeFormComponents.enterPositionName', { language: getCurrentLanguage()?.label })}
          value={getFieldValue('name')}
          onChange={(e) => handleFieldChange('name', e?.target?.value)}
          error={errors?.[`name_${activeLanguage}`]}
          disabled={disabled}
          className="w-full"
        />

        <Input
          label={t('officeFormComponents.positionTitle')}
          type="text"
          placeholder={t('officeFormComponents.enterOfficialTitle', { language: getCurrentLanguage()?.label })}
          value={getFieldValue('title')}
          onChange={(e) => handleFieldChange('title', e?.target?.value)}
          error={errors?.[`title_${activeLanguage}`]}
          disabled={disabled}
          className="w-full"
        />

        {/* <Input
          label={t('officeFormComponents.departmentDivision')}
          type="text"
          placeholder={t('officeFormComponents.enterDepartmentName', { language: getCurrentLanguage()?.label })}
          value={getFieldValue('department')}
          onChange={(e) => handleFieldChange('department', e?.target?.value)}
          error={errors?.[`department_${activeLanguage}`]}
          disabled={disabled}
          className="w-full"
        /> */}
      </div>
      {/* Translation Progress */}
      <div className="p-4 bg-muted/30 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-foreground">{t('officeFormComponents.translationProgress')}</h4>
          <span className="text-xs text-muted-foreground">
            {languages?.filter(lang => 
              formData?.[`name_${lang?.code}`] && formData?.[`title_${lang?.code}`]
            )?.length} / {languages?.length} {t('officeFormComponents.languages')}
          </span>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          {languages?.map((language) => {
            const hasName = formData?.[`name_${language?.code}`];
            const hasTitle = formData?.[`title_${language?.code}`];
            const isComplete = hasName && hasTitle;
            
            return (
              <div
                key={language?.code}
                className={`flex items-center space-x-2 p-2 rounded text-xs ${
                  isComplete 
                    ? 'bg-success/10 text-success' 
                    : hasName || hasTitle
                    ? 'bg-warning/10 text-warning' :'bg-muted text-muted-foreground'
                }`}
              >
                <span>{language?.flag}</span>
                <span className="font-medium">{language?.label}</span>
                <Icon 
                  name={isComplete ? 'CheckCircle' : hasName || hasTitle ? 'Clock' : 'Circle'} 
                  size={12} 
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MultilingualNameFields;